import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uid } from "uuid";
import {
  IAddress,
  IAppointmentTypeTemplate,
  IEventSlot,
} from "../../interfaces";
import { loggerService, networkService } from "../../services";
import dateService, {
  API_DATE_FORMAT,
  USER_TIME_FORMAT_12H,
} from "../../services/date.service";
import websocketLoggerService from "../../services/websocket-logger";
import { useConsumerStore } from "../../store/auth";
import { useFormStore } from "../../store/form";

interface ICalendarProps {
  setStep: (step: number) => void;
  userInfoLog: any;
  isRegistered: boolean;
}

const ScheduleSession: React.FC<ICalendarProps> = ({
  setStep,
  userInfoLog,
  isRegistered,
}) => {
  const navigate = useNavigate(),
    consumer = useConsumerStore((state) => state.consumer),
    referralCode = useFormStore((state) => state.referralCode),
    [selectedDate, setSelectedDate] = useState<string>(), // YYYY-MM-DD
    [selectedMonth, setSelectedMonth] = useState<string>(), // YYYY-MM-01
    [selectedSlot, setSelectedSlot] = useState<IEventSlot>(),
    [dataSource, setDataSource] = useState<Record<string, IEventSlot[]>>({});

  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth0();

  const authenticatedUser = {
    externalUserId: consumer?.id,
    authenticationId: user?.sub,
  };

  const [appointmentTypeTemplate, setAppointmentTypeTemplate] =
    useState<IAppointmentTypeTemplate>(null);
  const [addresses, setAddresses] = useState<IAddress[]>(null);
  const [selectedAddress, setSelectedAddress] = useState<IAddress>(null);
  const [availableSlots, setAvailableSlots] = useState<IEventSlot[]>(null);

  // fetch appointment templates types

  React.useEffect(() => {
    if (!referralCode) return null;
    // redirect to welcome page if referral code is not valid
  }, [referralCode]);

  React.useEffect(() => {
    const getAddresses = async (appointmentTypeId: string) => {
      console.log("getAddresses");

      networkService
        .get<IAddress[]>(
          `/api/scheduling/patients/${consumer?.id}/appointment-type-template-id/${appointmentTypeId}/addresses`
        )
        .then((addresses) => {
          const referralCodeKey = referralCode.split("-")[0];

          const addressesByReferralCode = addresses.filter((address) =>
            address?.title?.includes(referralCodeKey)
          );
          setAddresses(addressesByReferralCode);

          websocketLoggerService.sendMessage({
            ...authenticatedUser,
            eventType: `API request: getAddresses: GET /api/scheduling/patients/${consumer?.id}/appointment-type-template-id/${appointmentTypeId}/addresses`,
            eventSubType: `Get addresses`,
            eventData: "fetched available Adresses successfully",
          });
        })
        .catch((err) => {
          loggerService.error(err);
          // console.log(err.message);
          websocketLoggerService.sendMessage({
            ...authenticatedUser,
            eventType: `API request: getAddresses: GET /api/scheduling/patients/${consumer?.id}/appointment-type-template-id/${appointmentTypeId}/addresses`,
            eventSubType: `Error: ${JSON.stringify(err)}`,
            eventData: "Could not get adresses",
          });

          // toast.error("Something went wrong");
        });
    };

    const getAppointmentTypes = async () => {
      try {
        const appointmentTypeTemplates = await networkService.get<
          IAppointmentTypeTemplate[]
        >(`/api/scheduling/patients/${consumer?.id}/appointment-types`);
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request: appointmentTypeTemplates: GET /api/scheduling/patients/${consumer?.id}/appointment-types`,
          eventSubType: `Get appointmentTypeTemplates`,
          eventData: "successfully fetched available appointmentTypeTemplates",
        });
        // const introductoryCoachingSession = appointmentTypeTemplates.find(
        //   (item) => item.templateName === "demo-sanbox test template"
        // );

        const DiabetesTypeId = appointmentTypeTemplates.find(
          (item) =>
            item.appointmentTypeTemplateId === 8069 &&
            item.userId === "azo_ep11fd4f8fc7e655g6btqwewqy765876r76"
        );

        setAppointmentTypeTemplate(DiabetesTypeId);

        // getAddresses(appointmentTypeTemplates[0].id);
      } catch (error) {
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request: appointmentTypeTemplates: GET /api/scheduling/patients/${consumer?.id}/appointment-type-templates`,
          eventSubType: `Get appointmentTypeTemplates, Error ${JSON.stringify(
            error
          )}`,
          eventData: "Could not GET available appointmentTypeTemplates",
        });
      }
    };
    getAppointmentTypes();
  }, [consumer?.id, referralCode]);

  const getAvailableSlotsAfter3Days = () => {
    const currentDay = dateService.getMomentDate().format("dddd");
    if (currentDay === "Monday") {
      return 4;
    } else {
      return 6;
    }
  };

  // if only one address is present

  React.useEffect(() => {
    if (addresses?.length === 1) setSelectedAddress(addresses[0]);
  }, [addresses]);

  // fetch available slots
  React.useEffect(() => {
    // if (selectedAddress === null) return;
    const fetchAvailableSlots = async () => {
      const appointmentId: string = appointmentTypeTemplate?.id.toString();

      if (!appointmentId) return;

      const addressTitle: string = selectedAddress?.title || "";

      try {
        let availableSlots = await networkService.get<IEventSlot[]>(
          `/api/scheduling/patients/${
            consumer?.id
          }/available-slots?appointmentTypeId=${appointmentId}&fromDate=${dateService
            .getMomentDate()
            .format(API_DATE_FORMAT)}&toDate=${dateService
            .getMomentDate()
            .add(2, "months")
            .format(API_DATE_FORMAT)}`
        );

        // TODO sort by startDateTime
        const sortedSlots = availableSlots.sort((p1, p2) => {
          const startDateP1 = new Date(p1.startDateTime);
          const startDateP2 = new Date(p2.startDateTime);
          return startDateP1 < startDateP2
            ? -1
            : startDateP1 > startDateP2
            ? 1
            : 0;
        });

        availableSlots = sortedSlots;

        setAvailableSlots(availableSlots);
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `/api/scheduling/patients/${
            consumer?.id
          }/available-slots?appointmentTypeTemplateId=${appointmentId}&fromDate=${dateService
            .getMomentDate()
            .format(API_DATE_FORMAT)}&toDate=${dateService
            .getMomentDate()
            .add(2, "months")
            .format(API_DATE_FORMAT)}&addressTitle=${addressTitle}`,
          eventSubType: `Get availableSlots`,
          eventData: "successfully fetched available slots",
        });
      } catch (error) {
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `/api/scheduling/patients/${
            consumer?.id
          }/available-slots?appointmentTypeTemplateId=${appointmentId}&fromDate=${dateService
            .getMomentDate()
            .format(API_DATE_FORMAT)}&toDate=${dateService
            .getMomentDate()
            .add(2, "months")
            .format(API_DATE_FORMAT)}&addressTitle=${addressTitle}`,
          eventSubType: `Get availableSlots failed`,
          eventData: "Could not GETavailable slots",
        });
      }
    };
    fetchAvailableSlots();
  }, [appointmentTypeTemplate?.id, consumer?.id]);

  // init state variables
  React.useEffect(() => {
    const momentObj = dateService.getMomentDate();
    setSelectedSlot(null);
    setSelectedMonth(dateService.getStartOfMonth(momentObj));
  }, []);

  // populate date to slot dict - `dataSource`
  React.useEffect(() => {
    if (!availableSlots) return;
    if (!selectedDate) {
      const datetime = dateService.convertUTCDateToUserTimezone(
        availableSlots[0]?.date
      );
      const formattedDate = datetime.format(API_DATE_FORMAT);
      setSelectedDate(formattedDate);
    }

    const dataSource = {};
    availableSlots.forEach((slot) => {
      const browserDate = dateService.convertUtcDateTimeToBrowserFormat(
        slot.date,
        slot.startTime,
        "YYYYMMDD"
      );
      if (!dataSource[browserDate]) {
        dataSource[browserDate] = [];
      }
      dataSource[browserDate].push(slot);
    });

    setDataSource(dataSource);
  }, [availableSlots]);

  // const populateSlots = (slots, start, end) => {
  //   const availableSlots = slots?.reduce((acc, slot) => {
  //     const currentTime = dateService.convertToMomentObject(
  //       dateService.convertUtcDateTimeToBrowserFormat(
  //         slot.date,
  //         slot.startTime,
  //         USER_TIME_FORMAT_12H
  //       )
  //     );

  //     const startTime = dateService.convertToMomentObject(start);
  //     const endTime = dateService.convertToMomentObject(end);

  //     if (currentTime.isBetween(startTime, endTime)) {
  //       acc.push("1");
  //     }
  //     return acc;
  //   }, []);

  //   return availableSlots;
  // };

  const onDateSelect = (date: string) => {
    if (date === selectedDate) {
      return;
    }

    setSelectedSlot(null);
    setSelectedDate(date);
  };

  // const doesSlotExist = (
  //   dataSource,
  //   year: string,
  //   month: string,
  //   date: string
  // ) => {
  //   const momentObj = dateService.convertDateStrToMoment(
  //     `${year}-${month}-${date}`,
  //     API_DATE_FORMAT
  //   );
  //   const slots = dataSource?.[momentObj.format("YYYYMMDD")];

  //   const morningSlots = populateSlots(slots, "09:59 AM", "12:00 PM") || 0;
  //   const afternoonSlots = populateSlots(slots, "11:59 AM", "04:00 PM") || 0;
  //   const eveningSlots = populateSlots(slots, "03:59 PM", "11:59 PM") || 0;

  //   const morningSlotsLength: number = morningSlots?.length;
  //   const afterNoonSlotsLength: number = afternoonSlots?.length;
  //   const eveningSlotsLength: number = eveningSlots?.length;

  //   return (
  //     morningSlotsLength + afterNoonSlotsLength + eveningSlotsLength > 0 ||
  //     false
  //   );
  // };

  const renderMonthDates = (selectedMonth: string) => {
    const momentObj = dateService.convertDateStrToMoment(
        selectedMonth,
        API_DATE_FORMAT
      ),
      monthDates: string[] = [],
      month = momentObj.format("MM"),
      year = momentObj.format("YYYY"),
      daysInMonth = momentObj.daysInMonth();

    // populate empty weekdays at month start
    for (let i = 0; i < momentObj.day(); i++) {
      monthDates.push("");
    }

    // populate month dates
    for (let i = 1; i <= daysInMonth; i++) {
      monthDates.push(momentObj.format("DD"));
      momentObj.add(1, "days");
    }

    return (
      <div className="grid grid-cols-7 gap-4 mt-6 md:gap-6 place-items-center">
        <p className="text-xs text-gray-400 uppercase">Sun</p>
        <p className="text-xs text-gray-400 uppercase">Mon</p>
        <p className="text-xs text-gray-400 uppercase">Tue</p>
        <p className="text-xs text-gray-400 uppercase">Wed</p>
        <p className="text-xs text-gray-400 uppercase">Thr</p>
        <p className="text-xs text-gray-400 uppercase">Fri</p>
        <p className="text-xs text-gray-400 uppercase">Sat</p>
        {monthDates.map((date, index) => {
          const monthDate = `${year}-${month}-${date}`,
            // doesSlotsExists = doesSlotExist(dataSource, year, month, date);
            doesSlotsExists = dataSource?.[`${year}${month}${date}`];

          return !date ? (
            <span key={index}></span>
          ) : (
            <button
              key={index}
              disabled={!doesSlotsExists}
              onClick={() => onDateSelect(monthDate)}
              className={`flex items-center justify-center w-8 h-8 text-base text-gray-800 transition-colors duration-300 rounded-full md:text-lg hover:text-white hover:bg-primary-900 focus:outline-none ${
                monthDate === selectedDate ? "bg-primary-800 text-white" : ""
              } disabled:opacity-40 disabled:pointer-events-none`}>
              {date}
            </button>
          );
        })}
      </div>
    );
  };

  const moveLeft = () => {
    const momentObj = dateService
      .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
      .subtract(1, "months");
    setSelectedMonth(dateService.getStartOfMonth(momentObj));
  };

  const moveRight = () => {
    const momentObj = dateService
      .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
      .add(1, "months");
    setSelectedMonth(dateService.getStartOfMonth(momentObj));
  };

  const handleSubmit = async (slot: IEventSlot) => {
    // console.log("slot", slot);

    if (!slot) {
      // toast.error("Please select preferred location and slot");
      return null;
    }
    setLoading(true);
    // console.log("consumer", consumer);

    const options = {
      hour: "numeric",
      minute: "numeric",
      hour12: false,
      timeZone: "Asia/Singapore",
    };

    await networkService
      .post(`/api/scheduling/patients/${consumer?.id}/appointments`, {
        appointmentTypeId: appointmentTypeTemplate.id,
        note: `None`,
        timeZone: consumer?.timeZone,
        mobileNumber: consumer?.mobileNumber,
        date: slot.date,
        startDateTime: slot.startDateTime,
        endDateTime: slot.endDateTime,
        filter: {
          addressTitle: selectedAddress?.title,
        },
      })
      .then((res: any) => {
        // console.log("res", res);
        const valuesToLog =
          userInfoLog != null
            ? userInfoLog.concat([
                // res.id,
                // res.appointmentTypeId,

                moment(res.date).tz("Asia/Singapore").format("YYYY-MM-DD"),
                moment(res.startDateTime).tz("Asia/Singapore").format("HH:mm"),
                moment(res.endDateTime).tz("Asia/Singapore").format("HH:mm"),
                // res.startDateTime,
                // res.endDateTime,
                res.note,
                res.locationType,
                // res.locationInfo,
                res.status,
                // res.appointmentType.id,
                res.appointmentType.description,
                // res.appointmentType.schedulerSystemUserId,
                res.appointmentType.durationInMinutes,
                res.appointmentType.locationType,
                // res.appointmentType.userId,
              ])
            : userInfoLog.concat([
                // res.id,
                // res.appointmentTypeId,
                res.patientId,
                moment(res.date).tz("Asia/Singapore").format("YYYY-MM-DD"),
                moment(res.startDateTime).tz("Asia/Singapore").format("HH:mm"),
                moment(res.endDateTime).tz("Asia/Singapore").format("HH:mm"),
                // res.startDateTime,
                // res.endDateTime,
                res.note,
                res.locationType,
                // res.locationInfo,
                res.status,
                // res.appointmentType.id,
                res.appointmentType.description,
                // res.appointmentType.schedulerSystemUserId,
                res.appointmentType.durationInMinutes,
                res.appointmentType.locationType,
                // res.appointmentType.userId,
              ]);

        // console.log("valuesToLog", valuesToLog);
        setLoading(false);
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request:  POST /api/scheduling/patients/${consumer?.id}/appointments`,
          eventSubType: "Book appointment",
          eventData: "successfully booked appointment, navigating to /success",
        });

        navigate(`/success`, {
          state: {
            location: selectedAddress,
            date: slot.date,
            startDateTime: slot.startDateTime,
            endDateTime: slot.endDateTime,
            valuesToLog,
            isRegistered,
          },
        });
      })
      .catch((err: any) => {
        loggerService.error(err);
        // console.log(err.message);
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request:  POST /api/scheduling/patients/${consumer?.id}/appointments`,
          eventSubType: "Book appointment",
          eventData: "book appointment request failed",
        });
        setLoading(false);

        toast.error("Something went wrong");
      });
  };

  // to do refactor
  const [filteredSlots, setFilteredSlots] = useState<any>();
  useEffect(() => {
    // pass moment time to this function
    const renderTimeSlots = async (selectedDate: string) => {
      const momentObj = dateService.convertDateStrToMoment(
          selectedDate,
          API_DATE_FORMAT
        ),
        slots = dataSource?.[momentObj.format("YYYYMMDD")],
        isSlotSelected = function (slot: IEventSlot) {
          if (!selectedSlot) return false;
          return slot.startTime === selectedSlot.startTime;
        };

      if (!Array.isArray(slots) || !slots.length) {
        return <div></div>;
      }

      const populateSlots = async (slots, start, end) => {
        const availableSlots = await slots.reduce((acc, slot) => {
          const currentTime = dateService.convertToMomentObject(
            dateService.convertUtcDateTimeToBrowserFormat(
              slot.date,
              slot.startTime,
              USER_TIME_FORMAT_12H
            )
          );

          const startTime = dateService.convertToMomentObject(start);
          const endTime = dateService.convertToMomentObject(end);

          if (currentTime.isBetween(startTime, endTime)) {
            acc.push(
              <button
                key={uid()}
                onClick={() => setSelectedSlot(slot)}
                className={`w-full py-2.5 text-sm rounded-lg hover:border-transparent focus:outline-none border border-gray-300  hover:border-primary-900 hover:bg-[#EAEBFF] hover:text-primary-900 hover:font-semibold duration-300 transition-colors group ${
                  isSlotSelected(slot)
                    ? "border-primary-800 bg-[#EAEBFF] text-primary-800 font-semibold"
                    : "text-black"
                }`}>
                {dateService.convertUtcDateTimeToBrowserFormat(
                  slot.date,
                  slot.startTime,
                  USER_TIME_FORMAT_12H
                )}
              </button>
            );
          }
          return acc;
        }, []);

        return availableSlots;
      };

      const morningSlots = await populateSlots(slots, "00:59 AM", "12:00 PM");
      const afternoonSlots = await populateSlots(slots, "11:59 AM", "04:00 PM");
      const eveningSlots = await populateSlots(slots, "03:59 PM", "11:59 PM");

      return (
        <>
          <div>
            {/* Morning Slots */}
            {morningSlots.length > 0 && (
              <SlotsTemplate
                title="Morning"
                slots={morningSlots}
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.30125 13.125H13.6987C13.5525 12.2497 13.1006 11.4546 12.4234 10.8811C11.7461 10.3076 10.8874 9.99286 10 9.99286C9.11255 9.99286 8.25387 10.3076 7.57662 10.8811C6.89938 11.4546 6.44747 12.2497 6.30125 13.125ZM5 13.75C5 12.4239 5.52678 11.1521 6.46447 10.2145C7.40215 9.27678 8.67392 8.75 10 8.75C11.3261 8.75 12.5979 9.27678 13.5355 10.2145C14.4732 11.1521 15 12.4239 15 13.75C15 13.9158 14.9342 14.0747 14.8169 14.1919C14.6997 14.3092 14.5408 14.375 14.375 14.375H5.625C5.45924 14.375 5.30027 14.3092 5.18306 14.1919C5.06585 14.0747 5 13.9158 5 13.75ZM10 3.75C10.1658 3.75 10.3247 3.81585 10.4419 3.93306C10.5592 4.05027 10.625 4.20924 10.625 4.375V6.875C10.625 7.04076 10.5592 7.19973 10.4419 7.31694C10.3247 7.43415 10.1658 7.5 10 7.5C9.83424 7.5 9.67527 7.43415 9.55806 7.31694C9.44085 7.19973 9.375 7.04076 9.375 6.875V4.375C9.375 4.20924 9.44085 4.05027 9.55806 3.93306C9.67527 3.81585 9.83424 3.75 10 3.75ZM20 13.75C20 13.9158 19.9342 14.0747 19.8169 14.1919C19.6997 14.3092 19.5408 14.375 19.375 14.375H16.875C16.7092 14.375 16.5503 14.3092 16.4331 14.1919C16.3158 14.0747 16.25 13.9158 16.25 13.75C16.25 13.5842 16.3158 13.4253 16.4331 13.3081C16.5503 13.1908 16.7092 13.125 16.875 13.125H19.375C19.5408 13.125 19.6997 13.1908 19.8169 13.3081C19.9342 13.4253 20 13.5842 20 13.75ZM3.75 13.75C3.75 13.9158 3.68415 14.0747 3.56694 14.1919C3.44973 14.3092 3.29076 14.375 3.125 14.375H0.625C0.45924 14.375 0.300269 14.3092 0.183058 14.1919C0.065848 14.0747 0 13.9158 0 13.75C0 13.5842 0.065848 13.4253 0.183058 13.3081C0.300269 13.1908 0.45924 13.125 0.625 13.125H3.125C3.29076 13.125 3.44973 13.1908 3.56694 13.3081C3.68415 13.4253 3.75 13.5842 3.75 13.75ZM17.0712 6.67875C17.1884 6.79595 17.2542 6.9549 17.2542 7.12062C17.2542 7.28635 17.1884 7.4453 17.0712 7.5625L15.3038 9.33C15.2461 9.38969 15.1771 9.43731 15.1009 9.47006C15.0246 9.50282 14.9426 9.52006 14.8596 9.52078C14.7766 9.5215 14.6943 9.50569 14.6175 9.47426C14.5407 9.44284 14.4709 9.39643 14.4123 9.33775C14.3536 9.27906 14.3072 9.20928 14.2757 9.13247C14.2443 9.05566 14.2285 8.97336 14.2292 8.89037C14.2299 8.80739 14.2472 8.72537 14.2799 8.64912C14.3127 8.57287 14.3603 8.5039 14.42 8.44625L16.1875 6.67875C16.3047 6.56158 16.4636 6.49576 16.6294 6.49576C16.7951 6.49576 16.954 6.56158 17.0712 6.67875ZM5.58 9.33C5.4628 9.44717 5.30385 9.51299 5.13812 9.51299C4.9724 9.51299 4.81345 9.44717 4.69625 9.33L2.92875 7.5625C2.8149 7.44462 2.7519 7.28675 2.75333 7.12287C2.75475 6.959 2.82048 6.80224 2.93636 6.68636C3.05224 6.57048 3.209 6.50475 3.37287 6.50333C3.53675 6.5019 3.69462 6.5649 3.8125 6.67875L5.58 8.44625C5.69717 8.56345 5.76299 8.7224 5.76299 8.88812C5.76299 9.05385 5.69717 9.2128 5.58 9.33Z"
                      fill="black"
                    />
                  </svg>
                }
              />
            )}
            {/* Afternoon  Slots */}
            {afternoonSlots.length > 0 && (
              <SlotsTemplate
                title="Afternoon"
                slots={afternoonSlots}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 20 20">
                    <g clipPath="url(#clip0_95_2227)">
                      <path
                        fill="#161616"
                        fillRule="evenodd"
                        d="M10 13.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5zM10 15a5 5 0 100-10 5 5 0 000 10zm0-15a.625.625 0 01.625.625v2.5a.625.625 0 11-1.25 0v-2.5A.625.625 0 0110 0zm0 16.25a.624.624 0 01.625.625v2.5a.624.624 0 11-1.25 0v-2.5A.625.625 0 0110 16.25zM20 10a.624.624 0 01-.625.625h-2.5a.624.624 0 110-1.25h2.5A.625.625 0 0120 10zM3.75 10a.625.625 0 01-.625.625h-2.5a.625.625 0 110-1.25h2.5A.625.625 0 013.75 10zm13.321-7.071a.625.625 0 010 .884L15.304 5.58a.626.626 0 01-.884-.885l1.768-1.767a.625.625 0 01.883 0zM5.58 14.42a.625.625 0 010 .884L3.812 17.07a.625.625 0 01-.883-.884l1.767-1.767a.625.625 0 01.884 0zm11.491 2.651a.625.625 0 01-.884 0l-1.767-1.767a.625.625 0 01.884-.884l1.767 1.768a.625.625 0 010 .883zM5.58 5.581a.625.625 0 01-.884 0L2.93 3.812a.625.625 0 11.884-.883L5.58 4.696a.625.625 0 010 .885z"
                        clipRule="evenodd"></path>
                    </g>
                    <defs>
                      <clipPath id="clip0_95_2227">
                        <path fill="#fff" d="M0 0H20V20H0z"></path>
                      </clipPath>
                    </defs>
                  </svg>
                }
              />
            )}
            {eveningSlots.length > 0 && (
              <SlotsTemplate
                title="Evening"
                slots={eveningSlots}
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.30125 13.125H13.6987C13.5525 12.2497 13.1006 11.4546 12.4234 10.8811C11.7461 10.3076 10.8874 9.99286 10 9.99286C9.11255 9.99286 8.25387 10.3076 7.57662 10.8811C6.89938 11.4546 6.44747 12.2497 6.30125 13.125ZM5 13.75C5 12.4239 5.52678 11.1521 6.46447 10.2145C7.40215 9.27678 8.67392 8.75 10 8.75C11.3261 8.75 12.5979 9.27678 13.5355 10.2145C14.4732 11.1521 15 12.4239 15 13.75C15 13.9158 14.9342 14.0747 14.8169 14.1919C14.6997 14.3092 14.5408 14.375 14.375 14.375H5.625C5.45924 14.375 5.30027 14.3092 5.18306 14.1919C5.06585 14.0747 5 13.9158 5 13.75ZM10 3.75C10.1658 3.75 10.3247 3.81585 10.4419 3.93306C10.5592 4.05027 10.625 4.20924 10.625 4.375V6.875C10.625 7.04076 10.5592 7.19973 10.4419 7.31694C10.3247 7.43415 10.1658 7.5 10 7.5C9.83424 7.5 9.67527 7.43415 9.55806 7.31694C9.44085 7.19973 9.375 7.04076 9.375 6.875V4.375C9.375 4.20924 9.44085 4.05027 9.55806 3.93306C9.67527 3.81585 9.83424 3.75 10 3.75ZM20 13.75C20 13.9158 19.9342 14.0747 19.8169 14.1919C19.6997 14.3092 19.5408 14.375 19.375 14.375H16.875C16.7092 14.375 16.5503 14.3092 16.4331 14.1919C16.3158 14.0747 16.25 13.9158 16.25 13.75C16.25 13.5842 16.3158 13.4253 16.4331 13.3081C16.5503 13.1908 16.7092 13.125 16.875 13.125H19.375C19.5408 13.125 19.6997 13.1908 19.8169 13.3081C19.9342 13.4253 20 13.5842 20 13.75ZM3.75 13.75C3.75 13.9158 3.68415 14.0747 3.56694 14.1919C3.44973 14.3092 3.29076 14.375 3.125 14.375H0.625C0.45924 14.375 0.300269 14.3092 0.183058 14.1919C0.065848 14.0747 0 13.9158 0 13.75C0 13.5842 0.065848 13.4253 0.183058 13.3081C0.300269 13.1908 0.45924 13.125 0.625 13.125H3.125C3.29076 13.125 3.44973 13.1908 3.56694 13.3081C3.68415 13.4253 3.75 13.5842 3.75 13.75ZM17.0712 6.67875C17.1884 6.79595 17.2542 6.9549 17.2542 7.12062C17.2542 7.28635 17.1884 7.4453 17.0712 7.5625L15.3038 9.33C15.2461 9.38969 15.1771 9.43731 15.1009 9.47006C15.0246 9.50282 14.9426 9.52006 14.8596 9.52078C14.7766 9.5215 14.6943 9.50569 14.6175 9.47426C14.5407 9.44284 14.4709 9.39643 14.4123 9.33775C14.3536 9.27906 14.3072 9.20928 14.2757 9.13247C14.2443 9.05566 14.2285 8.97336 14.2292 8.89037C14.2299 8.80739 14.2472 8.72537 14.2799 8.64912C14.3127 8.57287 14.3603 8.5039 14.42 8.44625L16.1875 6.67875C16.3047 6.56158 16.4636 6.49576 16.6294 6.49576C16.7951 6.49576 16.954 6.56158 17.0712 6.67875ZM5.58 9.33C5.4628 9.44717 5.30385 9.51299 5.13812 9.51299C4.9724 9.51299 4.81345 9.44717 4.69625 9.33L2.92875 7.5625C2.8149 7.44462 2.7519 7.28675 2.75333 7.12287C2.75475 6.959 2.82048 6.80224 2.93636 6.68636C3.05224 6.57048 3.209 6.50475 3.37287 6.50333C3.53675 6.5019 3.69462 6.5649 3.8125 6.67875L5.58 8.44625C5.69717 8.56345 5.76299 8.7224 5.76299 8.88812C5.76299 9.05385 5.69717 9.2128 5.58 9.33Z"
                      fill="black"
                    />
                  </svg>
                }
              />
            )}
          </div>
        </>
      );
    };

    const getSlots = async () => {
      const _slots = await renderTimeSlots(selectedDate);
      setFilteredSlots(_slots);
    };

    getSlots();
  }, [dataSource, selectedDate, selectedSlot]);

  return (
    <div className="overflow-y-scroll no-scrollbar max-h-[94vh] bg-[#F6F6FB] flex-[2]">
      <section className="relative mt-8 flex flex-col px-12 max-lg:px-5 ">
        <h1 className="text-2xl font-bold text-gray-800 ">
          2. Schedule Introductory Session
        </h1>
        <p className="w-4/6 mt-5 text-sm">
          According to your response your profile has been enrolled in the
          &nbsp;
          <span className="font-semibold">
            {sessionStorage.getItem("program") || "Diabetes care management"},
          </span>{" "}
          lets schedule an introductory session to get familar with our Diabetes
          care management program.
        </p>
        <div className="mt-5">
          {/* <p>Preferred Location</p> */}
          <div className="flex items-start mt-2 gap-5 flex-wrap max-lg:flex-col">
            {addresses?.map((address, index) => (
              <div
                className={`border  rounded-md w-60 px-2 py-3 cursor-pointer flex gap-1 max-lg:w-full ${
                  selectedAddress?.id === address.id
                    ? "border-primary-800 bg-[#EAEBFF]"
                    : "border-gray-400 bg-white"
                }`}
                onClick={() => {
                  setSelectedAddress(address);
                }}
                key={address.id}>
                <input
                  id="default-radio-1"
                  type="radio"
                  checked={selectedAddress?.id === address.id}
                  value={address.title}
                  onChange={() => {
                    setSelectedAddress(address);
                  }}
                  name="default-radio"
                  className="w-4 h-4 text-primary-900 bg-transparent focus:ring-0 focus:ring-offset-0 border-gray-300  mt-1 "
                />
                <label
                  htmlFor={address.title}
                  className="ml-2 text-sm font-medium ">
                  <p className="font-bold">{address.title}</p>
                  {address?.line1}, {address?.line2}, {address?.country}{" "}
                  {address?.zipCode}
                </label>
              </div>
            ))}
          </div>
          {/* {!selectedAddress && addresses && (
            <div className=" mt-8 w-full flex items-center justify-start gap-2 bg-yellow-50 p-4 rounded-md">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="fill-yellow-900"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M13.936 0.5L19.5 6.067V13.937L13.936 19.5H6.066L0.5 13.936V6.066L6.066 0.5H13.936ZM13.107 2.5H6.894L2.501 6.895V13.108L6.894 17.502H13.107L17.501 13.108V6.894L13.107 2.501V2.5ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z" />
              </svg>

              <span className="text-black">
                Please select a location to check appointment availability.
              </span>
            </div>
          )} */}
        </div>

        <main className="mt-8 lg:flex flex-col pb-10 lg:items-start ">
          <div className="p-6 bg-white border border-gray-100 rounded-xl">
            <div className="flex items-center justify-between gap-8 w-full mx-auto text-lg text-gray-800 focus:outline-none">
              <button onClick={moveLeft} className=" focus:outline-none">
                <svg
                  width="9"
                  height="13"
                  viewBox="0 0 9 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.5 1L1.5 6.5L7.5 12"
                    stroke="#0038AE"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <span className="font-bold">
                {dateService
                  .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
                  .format("MMMM")}{" "}
                {dateService
                  .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
                  .format("YYYY")}{" "}
              </span>

              <button onClick={moveRight} className=" focus:outline-none">
                <svg
                  width="9"
                  height="13"
                  viewBox="0 0 9 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M1.5 12L7.5 6.5L1.5 1"
                    stroke="#0038AE"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {renderMonthDates(selectedMonth)}
          </div>
          {!selectedSlot && selectedAddress && (
            <div className="flex items-center justify-start w-full gap-2 mt-4 bg-yellow-50  p-4 rounded-md">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="fill-yellow-900"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M13.936 0.5L19.5 6.067V13.937L13.936 19.5H6.066L0.5 13.936V6.066L6.066 0.5H13.936ZM13.107 2.5H6.894L2.501 6.895V13.108L6.894 17.502H13.107L17.501 13.108V6.894L13.107 2.501V2.5ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z" />
              </svg>

              <span className="text-black">
                Please select a time slot to book an appointment.
              </span>
            </div>
          )}
          <div className="mt-8  w-4/5 ">{filteredSlots}</div>
        </main>
        {selectedSlot && (
          <div className="flex items-center justify-start gap-2 bg-yellow-50  p-4 rounded-md">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="fill-yellow-900"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M13.936 0.5L19.5 6.067V13.937L13.936 19.5H6.066L0.5 13.936V6.066L6.066 0.5H13.936ZM13.107 2.5H6.894L2.501 6.895V13.108L6.894 17.502H13.107L17.501 13.108V6.894L13.107 2.501V2.5ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z" />
            </svg>

            <span className="text-black">
              Please review your submission details before submitting this form.
            </span>
          </div>
        )}
        <footer className="py-8 flex items-center justify-start gap-x-7 max-lg:w-full max-lg:pr-16">
          {/* <button
            type="button"
            onClick={() => setStep(0)}
            className="w-max bg-white border border-[#AFAFAF] text-black px-12 py-3 mt-4  rounded font-bold">
            Back
          </button> */}

          <button
            disabled={!selectedSlot}
            type="button"
            onClick={async () => await handleSubmit(selectedSlot)}
            className="w-max bg-[#222424] px-12 py-3 mt-4 text-white rounded font-bold disabled:bg-gray-400 disabled:cursor-not-allowed">
            {loading ? (
              <div className="flex gap-2 items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <div>Submitting...</div>
              </div>
            ) : (
              "Submit"
            )}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default ScheduleSession;

const SlotsTemplate = ({ title, slots, icon }) => {
  return (
    <>
      <div className="flex gap-x-2 items-center">
        {icon}
        <h1>{title}</h1>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4 mb-4 md:grid-cols-3">
        {slots}
      </div>
    </>
  );
};
